import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { InvoiceSettings, UserInfo } from "@/types/auth";

type SetInvoicesSettingsPayload = {
  invoiceSettings: InvoiceSettings;
};

type AuthState = {
  isAuthenticated: boolean;
  newPasswordRequired?: {
    challenge: string;
    session: string;
    username: string;
  };
  userInfo: UserInfo | undefined;
  invoiceSettings: InvoiceSettings | undefined;
};

const initialState: AuthState = {
  isAuthenticated: false,
  userInfo: undefined,
  invoiceSettings: undefined,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    userLoggedOut: (state) => {
      state.isAuthenticated = false;
    },
    setNewPasswordChallenge: (state, action: PayloadAction<{
      challenge: string;
      session: string;
      username: string;
    }>) => {
      state.newPasswordRequired = action.payload;
    },
    clearNewPasswordChallenge: (state) => {
      state.newPasswordRequired = undefined;
    },
    setUserInfo: (state, action: PayloadAction<UserInfo | undefined>) => {
      state.userInfo = action.payload;
    },
    setInvoiceSettings: (state, action: PayloadAction<SetInvoicesSettingsPayload>) => {
      state.invoiceSettings = action.payload.invoiceSettings;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userLoggedOut, (_) => {
      return initialState;
    })
  },
});

export const { setIsAuthenticated, userLoggedOut } = authSlice.actions;
export const { setNewPasswordChallenge } = authSlice.actions;
export const { clearNewPasswordChallenge } = authSlice.actions;
export const { setUserInfo, setInvoiceSettings } = authSlice.actions;

export const selectIsAuthenticated = (state: RootState) =>
  state.auth.isAuthenticated;

export const selectNewPasswordRequired = (state: RootState) => state.auth.newPasswordRequired;
export const selectUserInfo = (state: RootState) => state.auth.userInfo;
export const selectInvoiceSettings = (state: RootState) => state.auth.invoiceSettings;

export default authSlice.reducer;
